// SVG → PNG export.
//
// Walks the SVG node, inlines computed styles (so CSS variables and class
// rules survive the serialize round-trip), draws into a 2x canvas with the
// suite background, and triggers a download. No DOM mutation on the live
// SVG — we clone first.

export async function downloadSvgAsPng(
  svg: SVGSVGElement,
  filename: string,
  options: { background?: string; scale?: number } = {},
): Promise<void> {
  const scale = options.scale ?? 2;
  const background = options.background ?? "#0a0d12";

  // Determine intrinsic size.
  const bbox = svg.getBoundingClientRect();
  const width = Math.ceil(bbox.width);
  const height = Math.ceil(bbox.height);

  // Clone and inline styles so the standalone SVG renders identically.
  const cloned = svg.cloneNode(true) as SVGSVGElement;
  cloned.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  cloned.setAttribute("width", String(width));
  cloned.setAttribute("height", String(height));
  inlineStyles(svg, cloned);

  const xml = new XMLSerializer().serializeToString(cloned);
  // unicode-safe base64
  const b64 = btoa(unescape(encodeURIComponent(xml)));
  const dataUrl = `data:image/svg+xml;base64,${b64}`;

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = (e) => reject(e);
    img.src = dataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no 2d context");
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  await new Promise<void>((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve();
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so Chrome has time to trigger the download.
      window.setTimeout(() => URL.revokeObjectURL(url), 1500);
      resolve();
    }, "image/png");
  });
}

/**
 * Copy computed styles from each node in `source` onto the matching node in
 * `target`. SVG-in-SVG (defs / use) is uncommon enough that we skip it; if
 * we add gradients later we'll inline `<defs>` separately.
 */
function inlineStyles(source: SVGSVGElement, target: SVGSVGElement): void {
  const sourceNodes = source.querySelectorAll<SVGElement>("*");
  const targetNodes = target.querySelectorAll<SVGElement>("*");
  for (let i = 0; i < sourceNodes.length; i += 1) {
    const sNode = sourceNodes[i];
    const tNode = targetNodes[i];
    if (!sNode || !tNode) continue;
    const computed = window.getComputedStyle(sNode);
    let inline = "";
    // Cherry-pick only the properties that matter to chart rendering — full
    // copy bloats the SVG by ~10× and includes UA-default noise.
    const props = [
      "fill",
      "stroke",
      "stroke-width",
      "stroke-linecap",
      "stroke-linejoin",
      "opacity",
      "font-family",
      "font-size",
      "font-weight",
      "letter-spacing",
      "text-anchor",
      "dominant-baseline",
    ];
    for (const p of props) {
      const value = computed.getPropertyValue(p);
      if (value) inline += `${p}:${value};`;
    }
    if (inline) tNode.setAttribute("style", inline);
  }
}

export function sanitizeFilename(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}
